import { RefObject, useCallback, useEffect, useRef } from 'react';

export interface UsePerTabHorizontalScrollOptions {
    /**
     * Pre-seed saved scroll positions per tab key (e.g. start each tab at horizontal scroll 0).
     */
    initialScrollLeftByKey?: Record<string, number>;
}

const SCROLL_SAVE_THROTTLE_MS = 100;
const SCROLL_RESTORE_DELAY_MS = 50;

/**
 * Persists horizontal `scrollLeft` on a shared scroll container per logical tab key.
 * Throttles scroll events while a tab is active; on tab change, saves the outgoing position
 * and restores the incoming tab's last position after a short delay.
 */
export function usePerTabHorizontalScroll(
    scrollContainerRef: RefObject<HTMLDivElement | null> | undefined,
    activeTabKey: string,
    options?: UsePerTabHorizontalScrollOptions,
) {
    const { initialScrollLeftByKey = {} } = options ?? {};
    const scrollPositions = useRef<Record<string, number>>({ ...initialScrollLeftByKey });
    const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        const scrollContainer = scrollContainerRef?.current;
        if (!scrollContainer) {
            return;
        }

        const handleScroll = () => {
            if (!scrollContainerRef?.current) {
                return;
            }

            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }

            scrollTimeoutRef.current = setTimeout(() => {
                if (scrollContainerRef.current) {
                    scrollPositions.current[activeTabKey] = scrollContainerRef.current.scrollLeft;
                }
            }, SCROLL_SAVE_THROTTLE_MS);
        };

        scrollContainer.addEventListener('scroll', handleScroll);
        return () => {
            scrollContainer.removeEventListener('scroll', handleScroll);
        };
    }, [scrollContainerRef, activeTabKey]);

    useEffect(() => {
        return () => {
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, []);

    const beginTabTransition = useCallback(
        (nextTabKey: string, applyChange: () => void) => {
            if (nextTabKey === activeTabKey) {
                return;
            }

            if (scrollContainerRef?.current) {
                scrollPositions.current[activeTabKey] = scrollContainerRef.current.scrollLeft;
            }

            applyChange();

            if (scrollContainerRef?.current) {
                setTimeout(() => {
                    if (scrollContainerRef.current) {
                        const savedPosition = scrollPositions.current[nextTabKey];
                        if (savedPosition !== undefined) {
                            scrollContainerRef.current.scrollLeft = savedPosition;
                        }
                    }
                }, SCROLL_RESTORE_DELAY_MS);
            }
        },
        [scrollContainerRef, activeTabKey],
    );

    return { beginTabTransition };
}
